import * as React from 'react';
import { useShowController } from 'react-admin';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card, CardContent, Typography, Grid, Box, Button,
  Breadcrumbs, Link, Divider, CircularProgress, Chip, Paper
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import InfoIcon from '@mui/icons-material/Info';
import EuroIcon from '@mui/icons-material/Euro';
import GroupsIcon from '@mui/icons-material/Groups';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import BrandingWatermarkIcon from '@mui/icons-material/BrandingWatermark';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { motion } from 'framer-motion';

const BooleanStatus = ({ value, label, trueIcon, falseIcon }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
    {value ? (trueIcon || <CheckCircleIcon color="success" />) : (falseIcon || <CancelIcon color="error" />)}
    <Typography variant="body1">{label}: <strong>{value ? 'Sì' : 'No'}</strong></Typography>
  </Box>
);

const DetailItem = ({ label, value, icon, subValue }) => (
  <Box sx={{ mb: 2 }}>
    <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      {icon} {label}
    </Typography>
    <Typography variant="body1" sx={{ fontWeight: 500 }}>
      {value !== undefined && value !== null && value !== '' ? value : '-'}
      {subValue && <Typography component="span" variant="caption" sx={{ ml: 1 }}>{subValue}</Typography>}
    </Typography>
  </Box>
);

const PricingCard = ({ title, price, duration, color, icon, isCustom = false, isActive = true }) => {
  const cardColor = isActive ? color : '#9e9e9e';
  const bgColor = isActive ? `linear-gradient(135deg, ${color}15 0%, ${color}05 100%)` : '#f5f5f5';

  return (
    <Paper
      elevation={isActive ? 3 : 1}
      sx={{
        p: 3,
        borderRadius: 3,
        background: bgColor,
        border: `2px solid ${isActive ? color + '30' : '#e0e0e0'}`,
        transition: 'all 0.3s ease',
        opacity: isActive ? 1 : 0.7,
        '&:hover': {
          transform: isActive ? 'translateY(-4px)' : 'none',
          boxShadow: isActive ? 6 : 1,
          borderColor: isActive ? color : '#e0e0e0'
        }
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>
          {title}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {!isActive && <Chip label="Inattivo" size="small" sx={{ height: 24, fontSize: '0.7rem' }} />}
          <Box sx={{ bgcolor: isActive ? `${color}20` : '#e0e0e0', p: 1, borderRadius: 2 }}>
            {icon}
          </Box>
        </Box>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
        <EuroIcon sx={{ fontSize: 24, color: cardColor }} />
        <Typography variant="h4" sx={{ fontWeight: 700, color: cardColor }}>
          {price}
        </Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        {duration}
      </Typography>
    </Paper>
  );
};

export const Tbl_pianiShow = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { record, isLoading } = useShowController({ resource: 'tbl-piani', id });

  if (isLoading || !record) {
    return (
      <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  // DEBUG: Verifica cosa arriva dal backend
  /* console.log('🔍 Piano record:', record);
  console.log('💰 prezziPersonalizzati:', record.prezziPersonalizzati);
 */
  // Determina lo stato del piano
  let statusColor = '#4caf50', statusLabel = 'Attivo';
  if (record.flAnnullato) { statusColor = '#f44336'; statusLabel = 'Annullato'; }
  else if (!record.flAttivo) { statusColor = '#ff9800'; statusLabel = 'Inattivo'; }
  else if (!record.flVisibile) { statusColor = '#9e9e9e'; statusLabel = 'Nascosto'; }

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#f6f7fb', minHeight: '100vh' }}>
      <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>

        {/* Breadcrumbs */}
        <Breadcrumbs sx={{ mb: 3 }}>
          <Link component="button" variant="body2" onClick={() => navigate('/piani')} sx={{ textDecoration: 'none' }}>
            Piani
          </Link>
          <Typography variant="body2" color="text.primary">Dettaglio</Typography>
        </Breadcrumbs>

        {/* Header */}
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                📦 {record.nome}
              </Typography>
              <Chip
                label={statusLabel}
                sx={{
                  bgcolor: `${statusColor}20`,
                  color: statusColor,
                  fontWeight: 600,
                  borderRadius: 2
                }}
              />
            </Box>
            <Typography variant="body2" color="text.secondary">
              ID: {record.id} • Ordinamento: {record.ordinamento || 0}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate('/piani')}>
              Indietro
            </Button>
            <Button variant="contained" startIcon={<EditIcon />} onClick={() => navigate(`/piani/${record.id}/edit`)}>
              Modifica
            </Button>
          </Box>
        </Box>

        <Grid container spacing={3}>
          {/* Pricing Section */}
          <Grid item xs={12}>
            <Card sx={{ borderRadius: 3, boxShadow: 2, mb: 3 }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                  <EuroIcon color="primary" />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>Prezzi e Abbonamenti</Typography>
                </Box>

                <Grid container spacing={3}>
                  {/* Prezzo Mensile */}
                  {record.prezzoMensile != null && (
                    <Grid item xs={12} sm={6} md={4}>
                      <PricingCard
                        title="Abbonamento Mensile"
                        price={parseFloat(record.prezzoMensile).toFixed(2)}
                        duration="al mese"
                        color="#4caf50"
                        icon={<CalendarMonthIcon sx={{ color: '#4caf50' }} />}
                      />
                    </Grid>
                  )}

                  {/* Prezzo Annuale */}
                  {record.prezzoAnnuale != null && (
                    <Grid item xs={12} sm={6} md={4}>
                      <PricingCard
                        title="Abbonamento Annuale"
                        price={parseFloat(record.prezzoAnnuale).toFixed(2)}
                        duration="all'anno"
                        color="#2196f3"
                        icon={<TrendingUpIcon sx={{ color: '#2196f3' }} />}
                      />
                    </Grid>
                  )}

                  {/* Prezzi Personalizzati */}
                  {record.prezziPersonalizzati && Array.isArray(record.prezziPersonalizzati) && record.prezziPersonalizzati.map((prezzo, idx) => (
                    <Grid item xs={12} sm={6} md={4} key={idx}>
                      <PricingCard
                        title={`Abbonamento ${prezzo.mesi} ${prezzo.mesi === 1 ? 'Mese' : 'Mesi'}`}
                        price={parseFloat(prezzo.prezzo).toFixed(2)}
                        duration={`per ${prezzo.mesi} ${prezzo.mesi === 1 ? 'mese' : 'mesi'}`}
                        color="#ff9800"
                        icon={<CalendarMonthIcon sx={{ color: '#ff9800' }} />}
                        isCustom={true}
                        isActive={prezzo.flAttivo !== false}
                      />
                    </Grid>
                  ))}

                  {/* Nessun prezzo */}
                  {!record.prezzoMensile && !record.prezzoAnnuale && (!record.prezziPersonalizzati || record.prezziPersonalizzati.length === 0) && (
                    <Grid item xs={12}>
                      <Paper sx={{ p: 3, textAlign: 'center', bgcolor: '#f5f5f5', borderRadius: 2 }}>
                        <Typography variant="body1" color="text.secondary">
                          Nessun prezzo configurato per questo piano
                        </Typography>
                      </Paper>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Main Info Card */}
          <Grid item xs={12} md={8}>
            <Card sx={{ borderRadius: 3, boxShadow: 2, mb: 3 }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                  <InfoIcon color="primary" />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>Informazioni Generali</Typography>
                </Box>

                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <DetailItem label="Nome Piano" value={record.nome} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <DetailItem
                      label="Gruppo"
                      value={record.gruppoId ? `ID: ${record.gruppoId}` : 'Nessun gruppo'}
                      icon={<GroupsIcon fontSize="small" />}
                    />
                  </Grid>

                  {record.DescrizioneCompleta && (
                    <Grid item xs={12}>
                      <Divider sx={{ my: 1 }} />
                      <DetailItem label="Descrizione Completa" value={record.DescrizioneCompleta} />
                    </Grid>
                  )}

                  {record.note && (
                    <Grid item xs={12}>
                      <DetailItem label="Note" value={record.note} />
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>

            {/* Limits Card */}
            <Card sx={{ borderRadius: 3, boxShadow: 2 }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>Limiti e Configurazioni</Typography>
                <Grid container spacing={3}>
                  <Grid item xs={6} md={4}>
                    <DetailItem label="Max Squadre" value={record.maxNumeroSquadre || 'Illimitate'} />
                  </Grid>
                  <Grid item xs={6} md={4}>
                    <DetailItem label="Max Infografiche" value={record.maxInfografiche || 'Illimitate'} />
                  </Grid>
                  <Grid item xs={6} md={4}>
                    <DetailItem label="Qualità Immagini" value={record.imgQuality || 'Standard'} />
                  </Grid>

                  <Grid item xs={6} md={3}>
                    <DetailItem label="Post/Giorno" value={record.maxPostGg || 'Illimitati'} />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <DetailItem label="Post/Settimana" value={record.maxPostWeek || 'Illimitati'} />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <DetailItem label="Post/Mese" value={record.maxPostMonth || 'Illimitati'} />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <DetailItem label="Ordinamento" value={record.ordinamento || 0} />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Sidebar / Settings */}
          <Grid item xs={12} md={4}>
            <Card sx={{ borderRadius: 3, boxShadow: 2, mb: 3 }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Stato e Impostazioni</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <BooleanStatus
                    value={record.flVisibile}
                    label="Visibile"
                    trueIcon={<VisibilityIcon color="success" />}
                    falseIcon={<VisibilityOffIcon color="error" />}
                  />
                  <BooleanStatus value={record.flAttivo} label="Attivo" />
                  <BooleanStatus
                    value={record.watermark}
                    label="Watermark"
                    trueIcon={<BrandingWatermarkIcon color="primary" />}
                  />
                  <BooleanStatus
                    value={record.flAnnullato}
                    label="Annullato"
                    falseIcon={<CheckCircleIcon color="success" />}
                    trueIcon={<CancelIcon color="error" />}
                  />
                </Box>
              </CardContent>
            </Card>

            <Card sx={{ borderRadius: 3, boxShadow: 2 }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Timestamp</Typography>
                <DetailItem
                  label="Creato il"
                  value={new Date(record.createdAt).toLocaleDateString('it-IT')}
                  subValue={new Date(record.createdAt).toLocaleTimeString('it-IT')}
                />
                <DetailItem
                  label="Aggiornato il"
                  value={new Date(record.updatedAt).toLocaleDateString('it-IT')}
                  subValue={new Date(record.updatedAt).toLocaleTimeString('it-IT')}
                />
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </motion.div>
    </Box>
  );
};
